import { all, takeEvery, takeLatest, fork, call, put } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

// action
import { dealerPriceActions, configActions } from '@redux/store/index'

// api
import { dealerPriceApi, ResType } from '@api'

// lib
import { Logger } from '@utils'

// type
import { Dealer, DealerPriceSheetRow, DealerPriceSaveType } from '@schemas'

const errorText = (res: ResType | undefined, fallback: string): string => {
	const data: any = res?.data
	const msg = data?.message ?? data
	const title = msg?.meta?.title
	return Array.isArray(title)
		? String(title[0])
		: typeof title === 'string'
		? title
		: msg?.errorMessage ?? fallback
}

function* fetchDealerList() {
	try {
		yield put(dealerPriceActions.DEALER_LIST_STATUS('loading'))
		const res: ResType | undefined = yield call(dealerPriceApi.getDealerList)
		if (res !== undefined && res.status === 200) {
			const list = (res.data as any[]).map(a => new Dealer(a))
			yield put(dealerPriceActions.DEALER_LIST(list))
			yield put(dealerPriceActions.DEALER_LIST_STATUS(list.length > 0 ? 'ok' : 'empty'))
		} else {
			yield put(dealerPriceActions.DEALER_LIST_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(dealerPriceActions.DEALER_LIST_STATUS('empty'))
	}
}

function* fetchSheet(customerId: string) {
	try {
		if (customerId === '') {
			yield put(dealerPriceActions.SHEET({ dealer: null, rows: [] }))
			yield put(dealerPriceActions.SHEET_STATUS('init'))
			return
		}
		yield put(dealerPriceActions.SHEET_STATUS('loading'))
		const res: ResType | undefined = yield call(dealerPriceApi.getDealerPriceSheet, {
			params: { customerId },
		})
		if (res !== undefined && res.status === 200) {
			const data: any = res.data
			yield put(
				dealerPriceActions.SHEET({
					dealer: new Dealer(data.dealer),
					rows: (data.rows as any[]).map(a => new DealerPriceSheetRow(a)),
				}),
			)
			yield put(dealerPriceActions.SHEET_STATUS('ok'))
		} else {
			yield put(dealerPriceActions.SHEET({ dealer: null, rows: [] }))
			yield put(dealerPriceActions.SHEET_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(dealerPriceActions.SHEET_STATUS('empty'))
	}
}

function* selectDealer(action: PayloadAction<string>) {
	yield call(fetchSheet, action.payload)
}

function* saveDealerPrice(
	action: PayloadAction<{ customerId: string; body: DealerPriceSaveType }>,
) {
	try {
		yield put(dealerPriceActions.IS_SAVING(true))
		const res: ResType | undefined = yield call(dealerPriceApi.saveDealerPrice, {
			params: { customerId: action.payload.customerId },
			body: action.payload.body,
		})
		if (res !== undefined && res.status < 400) {
			yield put(configActions.TOAST({ isShow: true, message: '납품가를 저장했습니다.' }))
			yield put(dealerPriceActions.SAVED())
		} else {
			yield put(
				configActions.TOAST({
					isShow: true,
					message: errorText(res, '납품가 저장에 실패했습니다.'),
				}),
			)
		}
		// 결과와 무관하게 다시 읽는다 — 화면이 먼저 그린 값을 서버 거절 시 되돌리기 위해
		yield all([call(fetchDealerList), call(fetchSheet, action.payload.customerId)])
	} catch (error) {
		Logger.warn(error)
	} finally {
		yield put(dealerPriceActions.IS_SAVING(false))
	}
}

function* mainSaga() {
	yield takeEvery(dealerPriceActions.TRIGGER_INIT_DEALER_PRICE_PAGE, fetchDealerList)
	yield takeLatest(dealerPriceActions.SELECT_DEALER, selectDealer)
	yield takeEvery(dealerPriceActions.SAVE_DEALER_PRICE, saveDealerPrice)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
